/**
 * Probation — the Zod shapes behind the employee contract's probation routes
 * (`getProbation`, `confirmProbation`, `extendProbation`, `listProbationExtensions`,
 * `cancelProbationExtension`; see `employee.contract.ts`).
 *
 * Split out of `employee.contract.ts` only to keep that file reviewable; the routes themselves stay
 * on the employee router because they act on one `Employment`. The rules these shapes carry
 * (`MAX_PROBATION_EXTENSIONS`, the 409 reasons) live in `../lib/probation.ts`.
 *
 * Probation is deliberately NOT effective-dated the way department/manager/designation are (see
 * invariant 5's exception for lifecycle flags, and the `ProbationExtension` model itself): each
 * extension REQUEST appends a history row recording the previous end date, and once APPROVED
 * `Employment.probationEndOn` moves to the new one (ADR 0027 decision 7 — a pending request does not
 * move it). The API resets `probationReminderSentAt` to null when an extension is applied so the
 * N-days-before reminder fires again against the new date.
 *
 * 409s from these routes carry a machine-readable `details.reason` (`PROBATION_CONFLICT_REASONS`):
 * `not_on_probation`, `extension_limit_reached` (`MAX_PROBATION_EXTENSIONS` approved rows),
 * `extension_pending`, `incomplete_required_tasks`, `review_mismatch`, `extension_not_pending`,
 * `concurrent_change`. The HTTP `code` stays the generic `CONFLICT`.
 */

import { z } from 'zod';
import { PROBATION_CONFLICT_REASONS } from '../lib/probation.js';
import {
  dateOnlySchema,
  employmentStatusSchema,
  instantSchema,
  uuidSchema,
} from '../schemas/common.js';
import { reviewOutcomeSchema, reviewStatusSchema } from './review.contract.js';

export const extendProbationBodySchema = z.object({
  newProbationEndOn: dateOnlySchema,
  reason: z.string().trim().min(1).max(500),
  /**
   * The probation-cycle `PerformanceReview` (outcome `extended`) that recommended this extension,
   * when there is one. Must belong to this employee and have `cycleType = 'probation'`, else 409
   * `review_mismatch`. Recorded as `ProbationExtension.reviewId`. Optional: HR may extend without a
   * review.
   */
  reviewId: uuidSchema.optional(),
});

/**
 * Confirms an employee out of probation. The server refuses — 409 `incomplete_required_tasks` with
 * the list in `details.blockers` — while any REQUIRED onboarding task is still open, and while an
 * extension request is pending (409 `extension_pending`). The client's disabled "Confirm" button is
 * a convenience, not the gate; `employee.updateStatus` applies the same gate to probation ->
 * confirmed.
 */
export const confirmProbationBodySchema = z.object({
  /** Probation-cycle review that recommended confirmation; recorded as
   * `Employment.confirmationReviewId`. Same ownership rule as `extendProbationBodySchema.reviewId`. */
  reviewId: uuidSchema.optional(),
  note: z.string().trim().max(500).optional(),
});

export const probationExtensionStatusSchema = z.enum([
  'pending',
  'approved',
  'rejected',
  'cancelled',
]);
export type ProbationExtensionStatus = z.infer<typeof probationExtensionStatusSchema>;

export const probationExtensionSchema = z.object({
  id: uuidSchema,
  employmentId: uuidSchema,
  previousEndOn: dateOnlySchema,
  newEndOn: dateOnlySchema,
  reason: z.string(),
  status: probationExtensionStatusSchema,
  /** Who requested it (`ProbationExtension.extendedByPersonId`, which now means the requester). */
  requestedByPersonId: uuidSchema,
  requestedByName: z.string(),
  /** The `@hrms/workflow` instance deciding it; null only for rows that pre-date the approval flow. */
  workflowInstanceId: uuidSchema.nullable(),
  decidedAt: instantSchema.nullable(),
  /** The probation-cycle review that recommended it, when one was linked. */
  reviewId: uuidSchema.nullable(),
  createdAt: instantSchema,
});
export type ProbationExtension = z.infer<typeof probationExtensionSchema>;

/** A REQUIRED onboarding task that is still open and therefore blocks confirmation. */
export const probationBlockerSchema = z.object({
  taskId: uuidSchema,
  processId: uuidSchema,
  title: z.string(),
  stageName: z.string(),
  assigneeRole: z.enum(['HR', 'MANAGER', 'IT', 'BUDDY', 'EMPLOYEE']),
  status: z.enum(['pending', 'in_progress', 'overdue']),
  dueDate: dateOnlySchema,
});
export type ProbationBlocker = z.infer<typeof probationBlockerSchema>;

/**
 * `details` of a probation 409. `errorBodySchema.details` is `unknown` (some of these routes throw
 * 409 for unrelated conflicts too), so clients narrow it with `parseProbationConflict()` instead of
 * trusting a cast.
 */
export const probationConflictDetailsSchema = z.object({
  reason: z.enum(PROBATION_CONFLICT_REASONS),
  /** Present for `incomplete_required_tasks`. */
  blockers: z.array(probationBlockerSchema).optional(),
  /** Present for `extension_limit_reached`. */
  maxExtensions: z.number().int().positive().optional(),
});
export type ProbationConflictDetails = z.infer<typeof probationConflictDetailsSchema>;

/** Narrow an error body's `details` to a probation conflict, or null if it is some other conflict. */
export function parseProbationConflict(details: unknown): ProbationConflictDetails | null {
  const parsed = probationConflictDetailsSchema.safeParse(details);
  return parsed.success ? parsed.data : null;
}

/**
 * Minimal probation status view returned by `confirmProbation`/`extendProbation`/`getProbation` —
 * just enough for the onboarding/employee-detail panel to refresh in place without a second round
 * trip to `get()`.
 */
export const probationSummarySchema = z.object({
  employmentId: uuidSchema,
  status: employmentStatusSchema,
  probationEndOn: dateOnlySchema.nullable(),
  confirmedOn: dateOnlySchema.nullable(),
  /** Null after a fresh extension (or if no reminder has fired yet for the current end date). */
  probationReminderSentAt: instantSchema.nullable(),
  /** APPROVED extensions only — the number that counts toward `maxExtensions`. */
  extensionCount: z.number().int().nonnegative(),
  /** `MAX_PROBATION_EXTENSIONS`, echoed so the UI never hard-codes the cap. */
  maxExtensions: z.number().int().positive(),
  /** The request awaiting a decision, if any; at most one exists at a time. */
  pendingExtension: probationExtensionSchema.nullable(),
  /** The review recorded when confirmation was applied; null if confirmed without one. */
  confirmationReviewId: uuidSchema.nullable(),
});
export type ProbationSummary = z.infer<typeof probationSummarySchema>;

/** `extendProbation` result: the refreshed summary plus the row just created (pending or approved). */
export const extendProbationResponseSchema = probationSummarySchema.extend({
  extension: probationExtensionSchema,
});
export type ExtendProbationResponse = z.infer<typeof extendProbationResponseSchema>;

/** A probation-cycle review of the employee, as far as the probation panel needs it (ONB-5). */
export const probationReviewLinkSchema = z.object({
  reviewId: uuidSchema,
  cycleLabel: z.string(),
  status: reviewStatusSchema,
  /** `PerformanceReview.outcome` — the reviewer's recommendation; null until one is recorded. */
  outcome: reviewOutcomeSchema.nullable(),
  outcomeEffectiveOn: dateOnlySchema.nullable(),
});
export type ProbationReviewLink = z.infer<typeof probationReviewLinkSchema>;

/**
 * Everything the probation panel shows for one employment: the summary, whether confirmation would
 * be accepted right now and what blocks it, and the probation reviews an outcome can be linked to.
 * Performance never writes to onboarding (ADR 0027 decision 9) — this only READS reviews.
 */
export const probationDetailSchema = probationSummarySchema.extend({
  /** True when `confirmProbation` would succeed now: on probation, no blockers, none pending. */
  canConfirm: z.boolean(),
  blockers: z.array(probationBlockerSchema),
  /** Probation-cycle reviews for this person, newest first. */
  reviews: z.array(probationReviewLinkSchema),
  /** The newest review whose `outcome` is set and not `n/a` — a suggestion, never applied
   * automatically. Null when there is none. */
  recommendation: probationReviewLinkSchema.nullable(),
});
export type ProbationDetail = z.infer<typeof probationDetailSchema>;

export const probationExtensionListSchema = z.object({
  /** Newest first, every status. */
  items: z.array(probationExtensionSchema),
  approvedCount: z.number().int().nonnegative(),
  maxExtensions: z.number().int().positive(),
});
export type ProbationExtensionList = z.infer<typeof probationExtensionListSchema>;
